import { useMatch, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  A3IncidenceCostCenterMembershipsDocument,
  PayrollIncidenceDetailsPageDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import Attachments from 'modules/bookkeepers/components/IncidenceInformation/Attachments'
import TaskDocuments from 'modules/bookkeepers/components/IncidenceInformation/TaskDocuments'
import useGetIncidenceTaskDocuments from 'modules/bookkeepers/hooks/useGetIncidenceTaskDocuments'
import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { useIncidenceDetail } from 'modules/bookkeepers/lib/IncidenceDetailContext'
import { FACTORIAL_APP, PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import TabsSelector from 'modules/payroll_bundle/components/TabsSelector'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { INCIDENCE_TYPES, LEAVE_CHANGE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import A3innuvaIncidence from 'modules/payroll_integrations_a3innuva/components/Incidence'
import DatevApiIncidence from 'modules/payroll_integrations_datev_api/components/Incidence'
import DatevLaudsIncidence from 'modules/payroll_integrations_datev_lauds/components/Incidence'

import IncidenceContent from './IncidenceContent'
import IncidenceRuleAlert from './_IncidenceRuleAlert'
import MoreInformation from './_MoreInformation'

const IncidenceDetailPage = () => {
  const { navigation } = useNavigation()

  const { id } = useParams<'id'>()
  const incidenceId = id
  const incidence = useCurrentIncidence()
  const isBookkeeperView = !!useMatch({ path: navigation.bookkeepers.fullPath(), end: false })
  const incidenceType = incidence?.name as keyof typeof INCIDENCE_TYPES
  const isManualIncidence = incidence?.name === INCIDENCE_TYPES.manual_incidence
  const isLeaveIncidence = Object.values(LEAVE_CHANGE_TYPES).includes(incidence?.name)
  const {
    loading: loadingDetails,
    incidenceDetailModel,
    incidenceFilesCollection,
  } = useIncidenceDetail()
  const companyId = incidence.legalEntity?.companyId

  const { data: costCentersData } = useQuery(A3IncidenceCostCenterMembershipsDocument, {
    variables: {
      employeeId: incidence.employeeId ?? '',
    },
    skip:
      !companyId ||
      (incidenceType !== INCIDENCE_TYPES.hiring && incidenceType !== INCIDENCE_TYPES.cost_center),
  })
  const listOfCostCenters = costCentersData?.costCenters.assignmentsConnection.nodes ?? []

  const { loading: loadingRuleEvaluation, data } = useQuery(PayrollIncidenceDetailsPageDocument, {
    variables: {
      id: incidenceId ?? '',
      companyId: companyId ?? '0',
      employeeId: incidence.employeeId ?? '0',
      legalEntityId: incidence.legalEntityId ?? '',
    },
    skip: !companyId,
  })
  const ruleEvaluation = data?.bookkeepersManagement?.incidencesConnection.nodes[0]?.ruleEvaluation
  const employeeGroups = data?.employees.employeeGroupsConnection.nodes || []
  const personalHiringDetails = data?.employees?.employee ? [data.employees.employee] : []
  const hasA3innuvaFeature =
    data?.features.featuresConnection.nodes.some(
      (feature) => feature.name === 'payroll_integrations_a3nom' && feature.enabled
    ) ?? false

  const canRequestEau =
    data?.employees?.employee?._policies?.createEauRequestOauthToken?.can || false

  const application = isBookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP

  // TODO: CHECK THIS. It's not used, comes from rebase.
  const hasDevSilaeApiFeature =
    data?.features.featuresConnection.nodes.some(
      (feature) => feature.name === 'dev_silae_api' && feature.enabled
    ) ?? false

  const { loading: loadingTaskDocuments, taskDocuments } = useGetIncidenceTaskDocuments({
    incidenceId: incidenceId ?? '',
  })

  const { hasCompensations: hasCompensationsModule } = useHasCompensations()

  const loading = loadingDetails || loadingRuleEvaluation || loadingTaskDocuments

  useEffect(() => {
    if (hasCompensationsModule && incidence?.name) {
      track(trackingEvents.compensations.checkDetailsInEmployeeUpdates, {
        update_type: incidence.name,
        tab: 'information',
        application: isBookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
      })
    }
  }, [hasCompensationsModule, incidence?.name, isBookkeeperView])

  return (
    <>
      <TabsSelector
        selectedTab="information"
        incidenceId={incidenceId ?? ''}
        isBookkeeper={isBookkeeperView}
      />
      {!loading && !isManualIncidence && (
        <>
          <A3innuvaIncidence
            incidenceType={incidenceType}
            incidenceDetail={incidenceDetailModel}
            employeeId={incidence.employeeId ?? ''}
            companyId={incidence.legalEntity?.companyId}
          />
          <DatevLaudsIncidence
            incidenceType={incidenceType}
            incidenceDetail={incidenceDetailModel}
            companyId={incidence.legalEntity?.companyId}
          />
          <DatevApiIncidence
            incidenceType={incidenceType}
            incidenceDetail={incidenceDetailModel}
            companyId={incidence.legalEntity?.companyId}
          />
        </>
      )}
      <Box overflowY="auto">
        {loading ? (
          <Loading />
        ) : (
          <>
            <Box component="ul" padding="s24" gap="s32">
              {ruleEvaluation && ruleEvaluation.validity !== 'valid' && (
                <IncidenceRuleAlert
                  employeeId={incidence.employeeId ?? ''}
                  basicView={isBookkeeperView}
                  ruleEvaluation={ruleEvaluation}
                />
              )}
              <IncidenceContent
                incidenceType={incidenceType}
                employeeGroups={employeeGroups}
                listOfCostCenters={listOfCostCenters}
                personalHiringDetails={personalHiringDetails}
                hasA3innuvaFeature={hasA3innuvaFeature}
                canRequestEau={canRequestEau}
                application={application}
              />
              {isBookkeeperView && taskDocuments.length > 0 && (
                <TaskDocuments taskDocuments={taskDocuments} bookkeeperView={isBookkeeperView} />
              )}
              {!isLeaveIncidence && incidenceFilesCollection.fromIncidenceMessage.length > 0 && (
                <Attachments
                  label={i18n.t('bookkeepers.incidence.details.attachments')}
                  files={incidenceFilesCollection.fromIncidenceMessage}
                />
              )}
            </Box>
            <MoreInformation incidence={incidence} />
          </>
        )}
      </Box>
    </>
  )
}

export default observer(IncidenceDetailPage)
